import { ColumnProps } from "antd/lib/table";
import { Button, Dropdown, Tag as TagAntd, Tooltip } from "antd";

import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import StoreContext from "../../../context/store/context";
import { IStore } from "../../../models/store/response";
import Space from "../../general/antd/space";
import ViewBtn from "../../general/table-components/actions/view-btn";
import EditBtn from "../../general/table-components/actions/edit-btn";
import DeleteBtn from "../../general/table-components/actions/delete-btn";
import eventManager, { EVENT_SUCCESS } from "../../../utils/events";
import { ISort, IStaticFilter } from "../../../models/base/base-query";
import { tableOnChange } from "../../../utils/helpers/table-sorts-filters";
import Table from "../../general/table-components/table";
import { ImCheckmark, ImCross } from "react-icons/im";
import {
  getStorStatus,
  getStorType,
  getStorWorkingStatus,
  getStoreStatusColor,
  getStoreTypeColor,
} from "../../../models/store/enum";
import Tag from "../../general/antd/tag";
import moment from "moment";
import { CopyOutlined, MoreOutlined } from "@ant-design/icons";
import FormHookModal from "../../general/modals/form-hook-modal";
import { useModal } from "react-modal-hook";
import CopyForm from "../copy-form";
import PrizeForm from "../prize-form";
import PrizeContext from "../../../context/prize/context";
import Popconfirm from "../../general/antd/pop-confirm";
import AppContext from "../../../context/app/context";

interface IProps {}

const StoreTable: React.FC<IProps> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { list, actions, loading, query } = useContext(StoreContext);
  const { screenSize } = useContext(AppContext);
  const {
    actions: actionPrize,
    loading: loadingPrize,
    query: queryPrize,
  } = useContext(PrizeContext);
  const [infoId, setInfoId] = useState<number>();
  const [showFormModal, hideForModal] = useModal(
    () => (
      <FormHookModal
        id="4"
        confirmButtonText={t("save")}
        loading={
          loading.includes("create") ||
          loading.includes("details") ||
          loading.includes("update")
        }
        title={t("copy_products_to")}
        hideModal={() => {
          hideForModal();
          setInfoId(undefined);
          actions.setDetails(undefined);
        }}
        width={"50vw"}
        style={{
          marginTop: "1.6rem",
          height: "59vh",
        }}
        onSubmit={async (data) => {
          await actions.copyStoreProducts({
            firstStoreId: infoId,
            secondStoreId: data?.secondStoreId,
          });
          setInfoId(undefined);
        }}
        defaultValues={{ secondStoreId: undefined }}
      >
        <CopyForm />
      </FormHookModal>
    ),
    [loading, infoId],
  );

  const [showAddPrizeModal, hideAddPrizeModal] = useModal(
    () => (
      <FormHookModal
        id="4"
        confirmButtonText={t("save")}
        loading={loadingPrize.includes("create")}
        title={t("add_prize_to")}
        hideModal={() => {
          hideAddPrizeModal();
          setInfoId(undefined);
          actions.setDetails(undefined);
        }}
        width={"50vw"}
        style={{
          marginTop: "1.6rem",
          height: "59vh",
        }}
        onSubmit={async (data) => {
          await actionPrize.createPrize({
            storeId: infoId,
            ...data,
          });
          setInfoId(undefined);
        }}
      >
        <PrizeForm />
      </FormHookModal>
    ),
    [loading, infoId],
  );

  const columns: ColumnProps<IStore>[] = [
    {
      title: t("id"),
      dataIndex: "id",
      key: "id",
      sorter: {
        multiple: 1,
      },
      width: 100,
      align: "center",
    },
    {
      title: t("name"),
      dataIndex: "name",
      align: "center",
      key: "name",
      width: 100,
      sorter: {
        multiple: 2,
      },
    },
    {
      title: t("city"),
      dataIndex: "city",
      align: "center",
      key: "city",
      width: 100,
      render: (_, record) => {
        return <Tag title={record?.city?.name} />;
      },
    },
    {
      title: t("status"),
      dataIndex: "status",
      align: "center",
      key: "status",
      width: 100,
      render: (_, record) => {
        return (
          <Tag
            title={getStorStatus(record?.status)}
            color={getStoreStatusColor(record?.status)}
          />
        );
      },
    },
    {
      title: t("first_phone"),
      dataIndex: "firstPhone",
      align: "center",
      key: "firstPhone",
      width: 100,
    },
    {
      title: t("points_balance"),
      dataIndex: "pointsBalance",
      align: "center",
      key: "pointsBalance",
      width: 100,
    },
    {
      title: t("type"),
      dataIndex: "type",
      align: "center",
      key: "type",
      width: 100,
      render: (_, record) => {
        return (
          <Tag
            title={getStorType(record?.type)}
            color={getStoreTypeColor(record?.type)}
          />
        );
      },
    },
    {
      title: t("pharmacy_promotion"),
      dataIndex: "pharmacyPromotion",
      align: "center",
      key: "pharmacyPromotion",
      width: 100,
      render: (_, record) => {
        return (
          <Tag
            title={record?.pharmacyPromotion ? t("active") : t("inactive")}
            color={record.pharmacyPromotion ? "green" : "red"}
          />
        );
      },
    },
    {
      title: t("is_namlieah"),
      dataIndex: "isNamlieah",
      align: "center",
      key: "isNamlieah",
      width: 120,
      render: (_, record) => {
        return (
          <Tag
            title={record?.isNamlieah ? t("yes") : t("no")}
            color={record?.isNamlieah ? "green" : "red"}
          />
        );
      },
    },
    {
      title: t("activating_public_service"),
      dataIndex: "activatingPublicService",
      align: "center",
      key: "activatingPublicService",
      width: 120,
      render: (_, record) => {
        return (
          <Tag
            title={record?.activatingPublicService ? t("yes") : t("no")}
            color={record?.activatingPublicService ? "green" : "red"}
          />
        );
      },
    },
    {
      title: t("work_status"),
      dataIndex: "workStatus",
      align: "center",
      key: "workStatus",
      width: 100,
      render: (_, record) => {
        return (
          <Tag
            title={getStorWorkingStatus(record?.workStatus)}
            color={getStoreStatusColor(record?.workStatus)}
          />
        );
      },
    },
    {
      title: t("free_delivery"),
      dataIndex: "freeDelivery",
      align: "center",
      key: "freeDelivery",
      width: 100,
      render: (_, record) => {
        return (
          <Tag
            title={record?.freeDelivery ? t("active") : t("inactive")}
            color={record.freeDelivery ? "green" : "red"}
          />
        );
      },
    },
    {
      title: t("numOfUnResponseOrder"),
      dataIndex: "numOfUnResponseOrder",
      key: "numOfUnResponseOrder",
      align: "center",
      width: 140,
    },
    {
      title: t("visibility_radius_meters"),
      dataIndex: "visibilityRadiusMeters",
      align: "center",
      key: "visibilityRadiusMeters",
      width: 140,
      sorter: {
        multiple: 2,
      },
    },
    {
      title: t("busy"),
      dataIndex: "busy",
      key: "busy",
      align: "center",
      width: 100,
      render: (busy) => {
        return (
          <>{busy ? <ImCheckmark color="green" /> : <ImCross color="red" />}</>
        );
      },
    },
    {
      title: t("created_at"),
      dataIndex: "createdAt",
      align: "center",
      key: "createdAt",
      width: 100,
      sorter: {
        multiple: 3,
      },
      render: (_, record) => {
        return moment(record?.createdAt).format("YYYY-MM-DD");
      },
    },
    {
      title: t("actions"),
      width: screenSize != "mobileOrTablet" ? 250 : 90,
      dataIndex: "",
      fixed: "right",
      align: "center",
      key: "x",
      render: (_: any, record) =>
        screenSize == "mobileOrTablet" ? (
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                {
                  key: "copy",
                  label: (
                    <Button
                      type="link"
                      onClick={() => {
                        setInfoId(record?.id);
                        showFormModal();
                      }}
                    >
                      {t("copy_products_to") + " " + t("stores")}
                    </Button>
                  ),
                },
                {
                  key: "view",
                  label: (
                    <Button
                      type="link"
                      onClick={(e) => {
                        const url = `/stores/details/${record?.id}`;
                        if (e.ctrlKey || e.metaKey) {
                          window.open(url, "_blank");
                        } else {
                          navigate(url);
                        }
                      }}
                    >
                      {t("view")}
                    </Button>
                  ),
                },
                {
                  key: "edit",
                  label: (
                    <Button
                      type="link"
                      onClick={(e) => {
                        const url = `/stores/update/${record?.id}`;
                        if (e.ctrlKey || e.metaKey) {
                          window.open(url, "_blank");
                        } else {
                          navigate(url);
                        }
                      }}
                    >
                      {t("edit")}
                    </Button>
                  ),
                },
                {
                  key: "delete",
                  label: (
                    <Popconfirm
                      title={t("confirm")}
                      onConfirm={async () => {
                        await actions.deleteStore(record?.id);
                        eventManager.emit(EVENT_SUCCESS);
                      }}
                    >
                      <Button type="link" danger>
                        {t("delete")}
                      </Button>
                    </Popconfirm>
                  ),
                },
              ],
            }}
          >
            <Button icon={<MoreOutlined />} />
          </Dropdown>
        ) : (
          <Space>
            <Tooltip title={t("copy_products_to") + " " + t("stores")}>
              <Button
                onClick={() => {
                  setInfoId(record?.id);
                  showFormModal();
                }}
                shape="circle"
                icon={<CopyOutlined />}
              />
            </Tooltip>

            <ViewBtn
              onClick={(e) => {
                const url = `/stores/details/${record?.id}`;
                if (e.ctrlKey || e.metaKey) {
                  window.open(url, "_blank");
                } else {
                  navigate(url);
                }
              }}
            />
            <EditBtn
              onClick={(e) => {
                const url = `/stores/update/${record?.id}`;
                if (e.ctrlKey || e.metaKey) {
                  window.open(url, "_blank");
                } else {
                  navigate(url);
                }
              }}
            />
            <DeleteBtn
              onConfirm={async () => {
                await actions.deleteStore(record?.id);
                eventManager.emit(EVENT_SUCCESS);
              }}
              loading={loading.includes("delete")}
            />
          </Space>
        ),
    },
  ];

  let tableFiltersProps = {};
  const [sorts, setSorts] = useState<ISort[]>([]);
  const [, setStaticFilters] = useState<IStaticFilter[]>(query?.filters ?? []);

  useEffect(() => {
    sorts.length > 0 &&
      actions.setQuery({
        ...query,
        orders: sorts,
      });
  }, [sorts]);

  return (
    <>
      <Table<any>
        rowKey="id"
        showPagination={true}
        columns={columns}
        dataSource={list?.data ?? []}
        loading={loading.includes("list") || list == undefined}
        size="small"
        total={list?.total}
        pageSize={query.perPage}
        page={query.page}
        scroll={{
          x: 1300,
        }}
        onPaginationChange={(page, pageSize) => {
          actions.setQuery({
            ...query,
            page,
            perPage: pageSize,
          });
        }}
        onChange={(_, tableFilters, tabelSorters) => {
          tableOnChange(
            tableFilters,
            tabelSorters,
            tableFiltersProps,
            setSorts,
            setStaticFilters,
          );
        }}
      />
    </>
  );
};

export default StoreTable;
